import { useState } from 'react';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useLang } from '@/lib/i18n';

export const consultingLeadSchema = z.object({
  name: z.string().trim().min(2, 'Mindestens 2 Zeichen').max(100),
  company: z.string().trim().max(100).optional().or(z.literal('')),
  email: z.string().trim().email('Ungültige E-Mail-Adresse').max(255),
  phone: z.string().trim().max(50).optional().or(z.literal('')),
  interest: z.string().min(1, 'Bitte wählen Sie ein Thema'),
  company_size: z.string().optional().or(z.literal('')),
  message: z.string().trim().max(2000).optional().or(z.literal('')),
  privacy: z.literal(true, {
    errorMap: () => ({ message: 'Bitte stimmen Sie der Datenschutzerklärung und den AGB zu' }),
  }),
});

export type ConsultingLeadFormData = z.infer<typeof consultingLeadSchema>;

const INTEREST_OPTIONS_DE = [
  'KI-Potenzialanalyse',
  'Strategieentwicklung',
  'EU AI Act Compliance',
  'Implementierung & Softwareentwicklung',
  'Lean Production & Shopfloor',
  'Change Management',
  'Schulung & Enablement',
  'ROI-Analyse',
  'Sonstiges',
] as const;

const INTEREST_OPTIONS_EN = [
  'AI potential analysis',
  'Strategy development',
  'EU AI Act compliance',
  'Implementation & software development',
  'Lean production & shopfloor',
  'Change management',
  'Training & enablement',
  'ROI analysis',
  'Other',
] as const;

const COMPANY_SIZE_OPTIONS_DE = [
  '1–10 Mitarbeiter',
  '11–50 Mitarbeiter',
  '51–250 Mitarbeiter',
  '250+ Mitarbeiter',
] as const;

const COMPANY_SIZE_OPTIONS_EN = [
  '1–10 employees',
  '11–50 employees',
  '51–250 employees',
  '250+ employees',
] as const;

export function useConsultingLeadForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const { toast } = useToast();
  const { uploadFiles, isUploading, progress: uploadProgress } = useFileUpload();
  const lang = useLang();

  const submitLead = async (data: ConsultingLeadFormData) => {
    setIsSubmitting(true);

    try {
      // 1. Upload attachments
      let attachmentPaths: string[] = [];
      if (attachments.length > 0) {
        attachmentPaths = await uploadFiles(attachments, 'consulting');
      }

      // 2. Save to DB
      const { error: dbError } = await supabase
        .from('consulting_leads')
        .insert({
          name: data.name,
          company: data.company || null,
          email: data.email,
          phone: data.phone || null,
          interest: data.interest,
          company_size: data.company_size || null,
          message: data.message || null,
          attachment_paths: attachmentPaths.length > 0 ? attachmentPaths : null,
        });

      if (dbError) throw dbError;

      // 3. Send email notification (fire & forget)
      supabase.functions.invoke('send-consulting-lead', {
        body: { ...data, attachment_paths: attachmentPaths, lang },
      }).catch(console.error);

      setIsSuccess(true);
      setAttachments([]);
      toast({
        title: lang === 'en' ? 'Request sent!' : 'Anfrage gesendet!',
        description: lang === 'en' ? 'We will get back to you within 24 hours.' : 'Wir melden uns innerhalb von 24 Stunden bei Ihnen.',
      });
    } catch (err) {
      console.error('Consulting lead error:', err);
      toast({
        title: lang === 'en' ? 'Sending failed' : 'Fehler beim Senden',
        description: err instanceof Error ? err.message : (lang === 'en' ? 'Please try again or contact us directly.' : 'Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitLead,
    isSubmitting,
    isUploading,
    uploadProgress,
    isSuccess,
    setIsSuccess,
    attachments,
    setAttachments,
    interestOptions: lang === 'en' ? INTEREST_OPTIONS_EN : INTEREST_OPTIONS_DE,
    companySizeOptions: lang === 'en' ? COMPANY_SIZE_OPTIONS_EN : COMPANY_SIZE_OPTIONS_DE,
  };
}
